import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchServices } from '../lib/api';

export default function ServicesOverview() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await fetchServices();
        setServices(data);
      } catch (err) {
        console.error('Error loading services:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    document.title = 'Vores ydelser - Røjsmose Isolering ApS';
    const metaDesc = document.querySelector('meta[name="description"]');
    const descText = 'Se vores professionelle isoleringsydelser med særligt fokus på rør- og ventilationsisolering samt ventilation til erhverv.';
    if (metaDesc) {
      metaDesc.setAttribute('content', descText);
    } else {
      const newMeta = document.createElement('meta');
      newMeta.name = 'description';
      newMeta.content = descText;
      document.head.appendChild(newMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="py-16 sm:py-24 bg-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Page Header */}
        <div className="border-b border-slate-200 pb-8 mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-brand-navy sm:text-5xl mt-2">
            Vores ydelser
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-3xl">
            Vi leverer energibesparende, støjreducerende og brandsikre løsninger med særligt fokus på <strong>rør- og ventilationsisolering samt ventilation til erhverv</strong>, industri, boligforeninger og entreprenører.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-2">
          {services.map((service) => {
            const serviceImage = service.image?.[0] || 'https://assets.auxodev.dk/assets/4ff98460-cf40-4566-82c8-07bb5181a02e/rojsmose-hero-isolering.jpg';
            return (
              <div
                key={service.id}
                className="group relative flex flex-col overflow-hidden rounded-none border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/30 hover:shadow-md"
              >
                {/* Image Section */}
                <div className="aspect-video w-full overflow-hidden bg-slate-100 rounded-none">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,height=338,fit=cover,quality=85,format=auto/${serviceImage.replace('https://assets.auxodev.dk/', '')}`}
                    alt={service.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-103 rounded-none"
                    loading="lazy"
                  />
                </div>

                {/* Content Section */}
                <div className="flex flex-1 flex-col p-6 sm:p-8">
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-50 text-brand-orange transition-colors duration-300 group-hover:bg-brand-orange group-hover:text-white">
                      <i className={`fa-solid ${service.icon || 'fa-shield-halved'} text-xl`} aria-hidden="true" />
                    </div>
                    <h2 className="text-2xl font-bold text-brand-navy group-hover:text-brand-orange transition-colors duration-200">
                      {service.title}
                    </h2>
                  </div>

                  <p className="mt-4 flex-1 text-slate-600 leading-relaxed">
                    {service.summary}
                  </p>

                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <Link
                      to={`/ydelser/${service.service_slug}`}
                      className="inline-flex items-center gap-2 text-sm font-bold text-brand-orange group-hover:text-brand-orange-hover transition-colors"
                    >
                      Læs mere om {service.title.toLowerCase()}
                      <i className="fa-solid fa-arrow-right transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-20 rounded-3xl bg-brand-navy p-8 sm:p-12 lg:p-16 text-white relative overflow-hidden shadow-xl">
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#f97316_1px,transparent_1px)] [background-size:16px_16px]" />
          
          <div className="relative z-10 max-w-3xl">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl mt-2">
              Har du et isoleringsprojekt i tankerne?
            </h2>
            <p className="mt-4 text-lg text-slate-300 leading-relaxed">
              Uanset om det drejer sig om en mindre efterisolering eller et stort erhvervsprojekt, står vi klar med professionel rådgivning og et uforpligtende tilbud.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-full bg-brand-orange px-6 py-3 text-base font-bold text-white shadow-sm transition-all duration-200 hover:bg-brand-orange-hover hover:scale-[1.02] active:scale-[0.98]"
              >
                Kontakt os i dag
              </Link>
              <a
                href="tel:42585848"
                className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/5 px-6 py-3 text-base font-bold text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/10 hover:border-white/40"
              >
                <i className="fa-solid fa-phone mr-2" aria-hidden="true" />
                Ring 42 58 58 48
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
